// Shared per-coin news WebSocket. The news WS protocol only supports one
// subscribed coin per connection (subscribe_coin / unsubscribe_coin pair),
// so we open one socket per active coin. Refcount drives lifecycle —
// when no widgets need ETH news anymore, the ETH socket closes.
//
// Multi-instance widgets on the same coin share that one socket. Previously
// every news widget opened its own — now it's one per (coin) regardless of
// widget count.

import { WS_BASE_URL } from '../../../utils/apiConfig';
import { createSharedStore } from './createSharedStore';

const MAX_NEWS = 20;
const RECONNECT_DELAY_MS = 5000;

const sockets = new Map();         // coin -> WebSocket
const reconnectTimers = new Map(); // coin -> setTimeout id

function getWsUrl() {
  if (typeof window === 'undefined') return null;
  const isDev = window.location.port === '5173' || window.location.hostname === 'localhost';
  if (isDev) {
    return WS_BASE_URL ? `${WS_BASE_URL}/news` : `ws://localhost:${window.location.port}/news`;
  }
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${WS_BASE_URL || `${protocol}//${window.location.host}`}/news`;
}

function formatRelativeTime(date) {
  const diffMs = Date.now() - new Date(date).getTime();
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return 'Just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return days === 1 ? '1 day ago' : days < 7 ? `${days} days ago` : new Date(date).toLocaleDateString();
}

function processItem(item) {
  return {
    ...item,
    time: formatRelativeTime(item.pubDate),
    url: item.url?.startsWith('http') ? item.url : `https://${item.url}`,
  };
}

function openSocket(coin, storeApi) {
  const url = getWsUrl();
  if (!url) return;
  const upper = coin.toUpperCase();
  let ws;
  try { ws = new WebSocket(url); } catch { scheduleReconnect(coin, storeApi); return; }
  sockets.set(coin, ws);

  ws.onopen = () => {
    try { ws.send(JSON.stringify({ type: 'subscribe_coin', coin: upper })); } catch {}
  };

  ws.onmessage = (event) => {
    try {
      const msg = JSON.parse(event.data);
      if (msg.type === 'initial_coin_news' && msg.coin === upper) {
        const news = (msg.data || []).map(processItem).slice(0, MAX_NEWS);
        storeApi.set(coin, { news, loading: false, error: null });
      } else if (msg.type === 'coin_news' && msg.data?.coins?.includes(upper)) {
        const item = processItem(msg.data);
        const cur = storeApi.get(coin);
        if (cur.news.some((n) => n.guid === item.guid)) return;
        storeApi.set(coin, { ...cur, news: [item, ...cur.news].slice(0, MAX_NEWS), loading: false });
      }
    } catch { /* malformed payload — drop */ }
  };

  ws.onerror = () => {
    // onclose handles reconnect
  };

  ws.onclose = () => {
    if (sockets.get(coin) !== ws) return; // released — don't reconnect
    sockets.delete(coin);
    scheduleReconnect(coin, storeApi);
  };
}

function scheduleReconnect(coin, storeApi) {
  clearTimeout(reconnectTimers.get(coin));
  const id = setTimeout(() => {
    // Only reconnect if someone still cares — release() may have been called.
    // The shared store's start/stop handles refcount; if stopStream removed
    // us we wouldn't be here. Belt-and-suspenders check via sockets map.
    if (!sockets.has(coin)) openSocket(coin, storeApi);
  }, RECONNECT_DELAY_MS);
  reconnectTimers.set(coin, id);
}

export const sharedCoinNews = createSharedStore({
  name: 'coinNews',
  emptyValue: () => ({ news: [], loading: true, error: null }),
  startStream(coin, storeApi) {
    openSocket(coin, storeApi);
  },
  stopStream(coin) {
    clearTimeout(reconnectTimers.get(coin));
    reconnectTimers.delete(coin);
    const ws = sockets.get(coin);
    if (ws) {
      sockets.delete(coin);
      try { ws.send(JSON.stringify({ type: 'unsubscribe_coin', coin: coin.toUpperCase() })); } catch {}
      try { ws.close(); } catch {}
    }
  },
});

export function useSharedCoinNews(coin) {
  return sharedCoinNews.useShared(coin);
}
